import * as React from "react";
import clsx from "clsx";
import { useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Save } from "lucide-react";
import { Text } from "../../../ui/components/Text";
import { Button } from "../../../ui/components/Button";
import { TextInput } from "../../../ui/components/Form";
import { Callout } from "../../../ui/components/Callout";
import { Card } from "../../../ui/components/Card";
import { useAuth } from "../../lib/auth/hooks";
import * as adminUsersApi from "../../lib/api/admin-users";
import { ApiClientError } from "../../lib/api/client";
import { ProApiClientError } from "../../lib/api/pro-api.client";
import { useI18n } from "../../lib/i18n";
import * as pageStyles from "../organizations/OrganizationDetailPage.css";
import * as styles from "../organizations/OrganizationEditPage.css";

function getErrorMessage(e: unknown, fallback: string): string {
  if (e instanceof ApiClientError || e instanceof ProApiClientError) return e.message;
  if (e instanceof Error) return e.message;
  return fallback;
}

function parseRoleIds(profile: Record<string, unknown>): { id: string; name: string }[] {
  const rolesRaw = profile.roles as
    | Array<{ role?: { id?: string; name?: string }; roleId?: string }>
    | undefined;
  if (!Array.isArray(rolesRaw)) return [];
  return rolesRaw
    .map((r) => {
      const role = r.role;
      const id = role?.id ?? r.roleId;
      const name = role?.name;
      if (!id || !name) return null;
      return { id: String(id), name: String(name) };
    })
    .filter((x): x is { id: string; name: string } => x != null);
}

export function UserEditPage({ userId }: { userId: string }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useI18n();
  const { accessToken, refreshAccessToken, refreshUser } = useAuth();
  const token =
    accessToken ??
    (typeof localStorage !== "undefined" ? localStorage.getItem("access_token") : null);

  const [profile, setProfile] = React.useState<Record<string, unknown> | null>(null);
  const [allRoles, setAllRoles] = React.useState<adminUsersApi.RoleSummary[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState<string | null>(null);

  // Identity fields
  const [firstName, setFirstName] = React.useState("");
  const [lastName, setLastName] = React.useState("");
  const [savedFirstName, setSavedFirstName] = React.useState("");
  const [savedLastName, setSavedLastName] = React.useState("");

  // Roles
  const [selectedRoleIds, setSelectedRoleIds] = React.useState<string[]>([]);
  const [savedRoleIds, setSavedRoleIds] = React.useState<string[]>([]);

  // Save state
  const [savingIdentity, setSavingIdentity] = React.useState(false);
  const [savingRoles, setSavingRoles] = React.useState(false);
  const [identityError, setIdentityError] = React.useState<string | null>(null);
  const [identityMessage, setIdentityMessage] = React.useState<string | null>(null);
  const [rolesError, setRolesError] = React.useState<string | null>(null);
  const [rolesMessage, setRolesMessage] = React.useState<string | null>(null);

  const load = React.useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setLoadError(null);
    try {
      const [raw, roles] = await Promise.all([
        adminUsersApi.getAdminUserById(token, userId),
        adminUsersApi.listRoles(token),
      ]);
      setProfile(raw);
      const fn = String(raw.firstName ?? "");
      const ln = String(raw.lastName ?? "");
      const roleIds = parseRoleIds(raw).map((r) => r.id);
      setFirstName(fn); setSavedFirstName(fn);
      setLastName(ln); setSavedLastName(ln);
      setSelectedRoleIds(roleIds); setSavedRoleIds(roleIds);
      setAllRoles(roles);
    } catch (e) {
      setLoadError(getErrorMessage(e, "Failed to load user"));
    } finally {
      setLoading(false);
    }
  }, [token, userId]);

  React.useEffect(() => { load(); }, [load]);

  const identityDirty =
    firstName.trim() !== savedFirstName.trim() ||
    lastName.trim() !== savedLastName.trim();

  const rolesDirty =
    selectedRoleIds.slice().sort().join(",") !== savedRoleIds.slice().sort().join(",");

  const handleSaveIdentity = async () => {
    if (!token || !identityDirty) return;
    setSavingIdentity(true);
    setIdentityError(null);
    setIdentityMessage(null);
    try {
      await adminUsersApi.patchAdminUser(token, userId, { firstName, lastName });
      setSavedFirstName(firstName);
      setSavedLastName(lastName);
      setIdentityMessage("Changes saved.");
      void queryClient.invalidateQueries({ queryKey: ['admin-users', 'list'] });
    } catch (e) {
      setIdentityError(getErrorMessage(e, "Failed to save."));
    } finally {
      setSavingIdentity(false);
    }
  };

  const handleSaveRoles = async () => {
    if (!token || !rolesDirty) return;
    setSavingRoles(true);
    setRolesError(null);
    setRolesMessage(null);
    try {
      await adminUsersApi.updateUserRoles(token, userId, selectedRoleIds);
      await refreshAccessToken();
      await refreshUser();
      setSavedRoleIds([...selectedRoleIds]);
      setRolesMessage("Roles updated.");
      void queryClient.invalidateQueries({ queryKey: ['admin-users', 'list'] });
    } catch (e) {
      setRolesError(getErrorMessage(e, "Failed to update roles."));
    } finally {
      setSavingRoles(false);
    }
  };

  const handleBack = () =>
    navigate({ to: "/workspace/users/$userId", params: { userId } });

  const renderSectionActions = (dirty: boolean, onSave: () => void, saving: boolean) => (
    <div className={clsx(styles.cardActions, !dirty && styles.cardActionsEnd)}>
      {dirty ? <span className={styles.changedBadge}>{t("pro.users.unsavedChanges")}</span> : null}
      <Button
        type="button"
        color="primary"
        variant="solid"
        iconLeft={<Save size={16} />}
        loading={saving}
        disabled={!dirty}
        onClick={onSave}
      >
        Save changes
      </Button>
    </div>
  );

  if (loading) {
    return (
      <div className={pageStyles.page}>
        <div className={styles.pageColumn}>
          <Text level="body-medium" style={{ color: "#9CA3AF" }}>{t("pro.users.loadingUser")}</Text>
        </div>
      </div>
    );
  }

  if (loadError || !profile) {
    return (
      <div className={pageStyles.page}>
        <div className={styles.pageColumn}>
          <button type="button" className={pageStyles.backLink} onClick={handleBack}>
            <ArrowLeft size={14} /> Back to user
          </button>
          <Callout variant="error">{loadError ?? "User not found."}</Callout>
        </div>
      </div>
    );
  }

  const email = String(profile.email ?? "");
  const fullName = [String(profile.firstName ?? ""), String(profile.lastName ?? "")].filter(Boolean).join(" ") || email;

  return (
    <div className={pageStyles.page}>
      <div className={styles.pageColumn}>
        <button type="button" className={pageStyles.backLink} onClick={handleBack}>
          <ArrowLeft size={14} /> {fullName}
        </button>

        <Text level="h1" weight="bold" className={styles.pageTitle}>
          Edit user
        </Text>
        <Text level="body-medium" style={{ color: "#6B7280", marginBottom: 24 }}>
          {email}
        </Text>

        <div className={styles.sections}>
          {/* Identity card */}
          <Card maxWidth={960} className={clsx(styles.sectionCard, identityDirty && styles.sectionCardChanged)}>
            <div className={styles.cardInner}>
              <div className={styles.sectionHeader}>
                <Text level="h3" weight="bold">{t("pro.users.identity")}</Text>
              </div>

              {identityError && <Callout variant="error">{identityError}</Callout>}
              {identityMessage && <Callout variant="success">{identityMessage}</Callout>}

              <div className={styles.twoColRow}>
                <TextInput
                  label={t("pro.users.firstName")}
                  value={firstName}
                  onChange={(e) => { setFirstName(e.target.value); setIdentityMessage(null); }}
                  disabled={savingIdentity}
                  changed={firstName.trim() !== savedFirstName.trim()}
                />
                <TextInput
                  label={t("pro.users.lastName")}
                  value={lastName}
                  onChange={(e) => { setLastName(e.target.value); setIdentityMessage(null); }}
                  disabled={savingIdentity}
                  changed={lastName.trim() !== savedLastName.trim()}
                />
              </div>

              {renderSectionActions(identityDirty, handleSaveIdentity, savingIdentity)}
            </div>
          </Card>

          {/* Roles card */}
          <Card maxWidth={960} className={clsx(styles.sectionCard, rolesDirty && styles.sectionCardChanged)}>
            <div className={styles.cardInner}>
              <div className={styles.sectionHeader}>
                <Text level="h3" weight="bold">{t("pro.users.roles")}</Text>
              </div>

              {rolesError && <Callout variant="error">{rolesError}</Callout>}
              {rolesMessage && <Callout variant="success">{rolesMessage}</Callout>}

              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {allRoles.map((role) => (
                  <label
                    key={role.id}
                    style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 14, cursor: savingRoles ? "not-allowed" : "pointer" }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedRoleIds.includes(role.id)}
                      onChange={() => {
                        setRolesMessage(null);
                        setSelectedRoleIds((prev) =>
                          prev.includes(role.id)
                            ? prev.filter((id) => id !== role.id)
                            : [...prev, role.id],
                        );
                      }}
                      disabled={savingRoles}
                    />
                    <span style={{ fontWeight: 500 }}>{role.name}</span>
                    {role.description && (
                      <span style={{ color: "#9CA3AF", fontSize: 12 }}>{role.description}</span>
                    )}
                  </label>
                ))}
              </div>

              {renderSectionActions(rolesDirty, handleSaveRoles, savingRoles)}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
